import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Eye, Gavel, Package, ShoppingBag, Trash2 } from 'lucide-react';
import './Marketplace.css';
import { marketplaceService } from '../../../../services';

const MyListings = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [listings, setListings] = useState([]);
    const [offers, setOffers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const clubIdFilter = searchParams.get('clubId');
    const sellerModeFilter = searchParams.get('sellerMode') || '';

    const fetchMarketplaceHub = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const [listingsData, offersData, transactionsData] = await Promise.all([
                marketplaceService.getMyListings(),
                marketplaceService.getReceivedOffers().catch(() => []),
                marketplaceService.getTransactions().catch(() => [])
            ]);
            setListings(listingsData);
            setOffers(offersData);
            setTransactions(transactionsData);
        } catch (fetchError) {
            console.error('Error loading seller marketplace data:', fetchError);
            setError(fetchError.message || 'Failed to load your marketplace data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMarketplaceHub();
    }, [fetchMarketplaceHub]);

    const removeListing = async (listingId) => {
        if (!window.confirm('Remove this listing from the marketplace?')) {
            return;
        }

        try {
            await marketplaceService.deleteListing(listingId);

            setListings((current) => current.filter((listing) => listing.listingId !== listingId));
        } catch (removeError) {
            console.error('Error removing listing:', removeError);
            setError('Failed to remove this listing.');
        }
    };

    const summary = useMemo(() => ({
        activeListings: listings.filter((listing) => listing.status === 'Active').length,
        pendingOffers: offers.filter((offer) => offer.status === 'Pending').length,
        activeTransactions: transactions.filter((transaction) => transaction.status !== 'Completed').length,
        completedSales: transactions.filter((transaction) => transaction.status === 'Completed').length
    }), [listings, offers, transactions]);

    const filteredListings = useMemo(() => {
        return listings.filter((listing) => {
            if (clubIdFilter && String(listing.clubId) !== String(clubIdFilter)) {
                return false;
            }
            if (sellerModeFilter === 'venue' && listing.sellerType !== 'Venue') {
                return false;
            }
            if (sellerModeFilter === 'member' && listing.sellerType !== 'Member') {
                return false;
            }
            return true;
        });
    }, [listings, clubIdFilter, sellerModeFilter]);

    const activeFilterClubName = useMemo(() => {
        if (!clubIdFilter) {
            return null;
        }
        return filteredListings.find((listing) => String(listing.clubId) === String(clubIdFilter))?.sellerName || null;
    }, [clubIdFilter, filteredListings]);

    const clearFilters = () => {
        setSearchParams({});
    };

    if (isLoading) {
        return (
            <div className="marketplace-page">
                <div className="listing-detail-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading your marketplace hub...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="marketplace-page">
            <button className="back-button" onClick={() => navigate('/dashboard/marketplace')}>
                <ArrowLeft size={20} />
                Back to Marketplace
            </button>

            <div className="mp-header">
                <div className="mp-header-content">
                    <div className="mp-header-icon">
                        <ShoppingBag size={28} />
                    </div>
                    <div>
                        <h1>My Listings</h1>
                        <p>Manage your live listings, incoming offers, and sales activity.</p>
                    </div>
                </div>
            </div>

            {(clubIdFilter || sellerModeFilter) && (
                <div className="mp-disclaimer-banner" style={{ marginBottom: '16px' }}>
                    <Package size={20} />
                    <div className="mp-disclaimer-content">
                        <strong>Filtered listings view</strong>
                        <p>
                            {clubIdFilter
                                ? `Showing marketplace listings for ${activeFilterClubName || 'the selected venue'}.`
                                : 'Showing listings for the selected seller mode.'}
                        </p>
                    </div>
                    <button onClick={clearFilters}>Clear</button>
                </div>
            )}

            {error && (
                <div className="mp-disclaimer-banner mp-error-banner">
                    <AlertTriangle size={20} />
                    <div className="mp-disclaimer-content">
                        <strong>Marketplace issue</strong>
                        <p>{error}</p>
                    </div>
                </div>
            )}

            <div className="mp-seller-summary">
                <div className="mp-seller-card">
                    <span>Active listings</span>
                    <strong>{summary.activeListings}</strong>
                </div>
                <div className="mp-seller-card">
                    <span>Pending offers</span>
                    <strong>{summary.pendingOffers}</strong>
                </div>
                <div className="mp-seller-card">
                    <span>Open transactions</span>
                    <strong>{summary.activeTransactions}</strong>
                </div>
                <div className="mp-seller-card">
                    <span>Completed sales</span>
                    <strong>{summary.completedSales}</strong>
                </div>
            </div>

            <section className="mp-seller-section">
                <div className="mp-seller-section-header">
                    <h2>Your listings</h2>
                    <button
                        className="mp-btn-primary"
                        onClick={() => {
                            if (clubIdFilter) {
                                navigate(`/dashboard/marketplace/create?clubId=${clubIdFilter}&sellerMode=venue`);
                                return;
                            }
                            navigate('/dashboard/marketplace/create');
                        }}
                    >
                        New Listing
                    </button>
                </div>
                {filteredListings.length > 0 ? (
                    <div className="mp-seller-list">
                        {filteredListings.map((listing) => (
                            <div key={listing.listingId} className="mp-seller-item">
                                <div className="mp-seller-item-main">
                                    <h3>{listing.title}</h3>
                                    <p>{listing.category} · {listing.condition || 'Condition not set'} · £{listing.price?.toLocaleString()}</p>
                                    <div className="mp-seller-badges">
                                        <span>{listing.status}</span>
                                        <span>{listing.sellerType === 'Venue' ? 'Venue Listing' : 'Member Listing'}</span>
                                        <span>{listing.viewCount || 0} views</span>
                                        <span>{listing.favouriteCount || 0} favourites</span>
                                    </div>
                                </div>
                                <div className="mp-seller-item-actions">
                                    <button className="mp-btn-secondary" onClick={() => navigate(`/dashboard/marketplace/${listing.listingId}`)}>
                                        <Eye size={16} />
                                        View
                                    </button>
                                    <button className="mp-btn-secondary" onClick={() => removeListing(listing.listingId)}>
                                        <Trash2 size={16} />
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="mp-seller-empty">
                        <Package size={44} />
                        <h3>No listings yet</h3>
                        <p>Create your first marketplace listing to start selling.</p>
                    </div>
                )}
            </section>

            <div className="mp-seller-grid">
                <section className="mp-seller-section">
                    <div className="mp-seller-section-header">
                        <h2>Offers received</h2>
                        <button className="mp-btn-secondary" onClick={fetchMarketplaceHub}>
                            Refresh
                        </button>
                    </div>
                    {offers.length > 0 ? (
                        <div className="mp-seller-list compact">
                            {offers.slice(0, 6).map((offer) => (
                                <div key={offer.offerId} className="mp-seller-item">
                                    <div className="mp-seller-item-main">
                                        <h3>{offer.listingTitle}</h3>
                                        <p>{offer.buyerName} offered £{offer.offerAmount?.toLocaleString()}</p>
                                        <div className="mp-seller-badges">
                                            <span>{offer.status}</span>
                                            <span>{new Date(offer.createdAt).toLocaleDateString('en-GB')}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="mp-seller-empty small">
                            <Gavel size={40} />
                            <p>No offers received yet.</p>
                        </div>
                    )}
                </section>

                <section className="mp-seller-section">
                    <div className="mp-seller-section-header">
                        <h2>Transactions</h2>
                        <button className="mp-btn-secondary" onClick={fetchMarketplaceHub}>
                            Refresh
                        </button>
                    </div>
                    {transactions.length > 0 ? (
                        <div className="mp-seller-list compact">
                            {transactions.slice(0, 6).map((transaction) => (
                                <div key={transaction.transactionId} className="mp-seller-item">
                                    <div className="mp-seller-item-main">
                                        <h3>{transaction.listingTitle}</h3>
                                        <p>£{transaction.salePrice?.toLocaleString()} · {transaction.buyerName}</p>
                                        <div className="mp-seller-badges">
                                            <span>{transaction.status}</span>
                                            <span>{new Date(transaction.createdAt).toLocaleDateString('en-GB')}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="mp-seller-empty small">
                            <ShoppingBag size={40} />
                            <p>No transactions yet.</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default MyListings;
